import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import Colors from '../../utils/color';
import CustomerPulse from '../CustomerPulse';
import LiveTranscriptCard from './LiveTranscriptCard';
import useLeftStackCollapse from './hooks/useLeftStackCollapse.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  leftPane: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  // The left pane is a flush stack (Customer Pulse + Live Transcript): no outer
  // frame or padding — the sections attach edge-to-edge and are separated only
  // by the hairline resize divider below.
  leftCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // Wraps each card inside a vertical panel: fills the panel height so each
  // card's OWN internal scroll works. overflow hidden (not auto) — the cards
  // scroll internally now, so this wrapper must never show its own (outer)
  // scrollbar, which otherwise appeared beside a collapsed hug-height card.
  leftStackPane: {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    overflowX: 'hidden',
  },
  // Hairline divider between the stacked left-pane cards (Customer Pulse / Live
  // Transcript). A full-width 1px rule (::before) attaches the two sections; a
  // short grip pill (::after) centred on the line signals it can be dragged to
  // grow/shrink the neighbouring card, growing + turning blue on hover/drag.
  vResizeHandle: {
    position: 'relative',
    height: spacing(1.25),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    backgroundColor: Colors.white,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      height: 1,
      backgroundColor: Colors.alto,
    },
    '&::after': {
      content: '""',
      zIndex: 1,
      height: 3,
      width: spacing(3.5),
      borderRadius: 2,
      backgroundColor: Colors.silverChalice,
      transition: 'background-color 120ms ease, width 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
      width: spacing(5),
    },
  },
}));

interface LeftSidebarProps {
  // Per-agent storage key; the nested vertical stack persists under a suffix.
  paneStorageKey: string;
}

/**
 * LEFT SIDEBAR of the HITL console. One card holding a drag-to-resizable
 * vertical stack of Live Transcript (top) + Customer Pulse (bottom) (sizes
 * persist per agent via autoSaveId). The Pulse strip folds in the metrics, signal/reminder chips
 * and the AI co-pilot ask (formerly the separate Action Center card), so the
 * Transcript gets the bulk of the height. On an unsupervised AI task arrival the
 * left PANE widens to 50% (see usePaneLayout) — keeping menu + cart visible —
 * while supervised keeps the agent's saved pane sizes. Collapse-sync between each
 * card's chevron and its panel lives in useLeftStackCollapse.
 */
const LeftSidebar = ({ paneStorageKey }: LeftSidebarProps) => {
  const classes = useStyles();
  const { cpOpen, setCpOpen, ltOpen, setLtOpen, cpPanelRef, ltPanelRef } =
    useLeftStackCollapse();

  return (
    <div className={classes.leftPane}>
      <div className={classes.leftCard}>
        {/* Left-pane cards (Live Transcript on top, Customer Pulse strip below)
            are drag-to-resizable; sizes persist per agent via autoSaveId. A
            distinct key (`:left-stack-3z`) so sizes saved under the previous
            order (Pulse on top) don't get applied inverted after the swap. */}
        <PanelGroup
          direction="vertical"
          autoSaveId={`${paneStorageKey}:left-stack-3z`}
        >
          <Panel
            id="lt"
            order={1}
            ref={ltPanelRef}
            collapsible
            collapsedSize={7}
            defaultSize={76}
            minSize={20}
            onCollapse={() => setLtOpen(false)}
            onExpand={() => setLtOpen(true)}
          >
            <div className={classes.leftStackPane}>
              <LiveTranscriptCard open={ltOpen} onOpenChange={setLtOpen} />
            </div>
          </Panel>
          <PanelResizeHandle className={classes.vResizeHandle} />
          {/* collapsedSize is larger than the transcript's (7): the collapsed
              Pulse strip keeps its AI status band below the header, so it needs
              ~2x the height. Stays below minSize so collapse still snaps in. */}
          <Panel
            id="cp"
            order={2}
            ref={cpPanelRef}
            collapsible
            collapsedSize={11}
            defaultSize={24}
            minSize={14}
            onCollapse={() => setCpOpen(false)}
            onExpand={() => setCpOpen(true)}
          >
            <div className={classes.leftStackPane}>
              <CustomerPulse open={cpOpen} onOpenChange={setCpOpen} />
            </div>
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
};

export default LeftSidebar;
